import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';

import { FormGroupComponent } from 'src/app/shared/components/form-group/form-group.component';
import { MainLayoutComponent } from 'src/app/shared/components/main-layout/main-layout.component';
import { DataService } from 'src/app/services/data.service';
import { ERROR_MESSAGES } from 'src/app/shared/constants/text-field';
import { Observable, Subscription, map, min, of } from 'rxjs';
import { FormData, Property } from 'src/app/services/data.model';
import { NAVIGATION_MAP, PAGES } from 'src/app/shared/constants/navigation-map';
import { NextButtonStateService } from 'src/app/services/next-button-state.service';
import { CheckboxComponent } from 'src/app/shared/components/checkbox/checkbox.component';
import { CounterComponent } from 'src/app/shared/components/counter/counter.component';
import {
  COLORS,
  DOOR_COLORS,
  DOOR_STYLES,
  DOOR_TYPES,
  LOCATIONS_LIST,
  SLIDING_STYLES,
  STYLES,
  TYPES_LIST,
  WINDOW_STYLES,
} from 'src/app/shared/constants/constants';

type Item = 'windows' | 'doors';

type Option = {
  path: string;
  name: string;
};

type OptionConfig = {
  [key: string]: Option[];
};

type WindowOption = { text: string };
type WindowStyles = {
  text: string;
  itemsList: WindowOption[];
};

@Component({
  selector: 'app-project-details',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MainLayoutComponent,
    FormGroupComponent,
    NgSelectModule,
    CheckboxComponent,
    CounterComponent,
  ],
  templateUrl: './project-details.component.html',
  styleUrl: './project-details.component.scss',
})
export class ProjectDetailsComponent implements OnInit, OnDestroy {
  projectDetailsForm: FormGroup;
  previouslyFilledData: {
    projectDetails: any;
  };
  windows = [];
  windowForms: FormGroup[] = [];
  doorForms: FormGroup[] = [];
  subscription: Subscription = new Subscription();
  isWindowsFormInvalid = false;
  isDoorsFormInvalid = false;
  stylesList: WindowStyles[] = WINDOW_STYLES;
  typesList = TYPES_LIST;
  locationsList = LOCATIONS_LIST;
  colors: {
    [key: string]: Option[];
  } = COLORS;
  styles: OptionConfig = STYLES;
  slidingStyles: OptionConfig = SLIDING_STYLES;
  error_messages = ERROR_MESSAGES;
  itemTypes = {
    WINDOWS: 'windows',
    DOORS: 'doors',
  };
  counterMinValue = 1;
  counterMaxValue = 50;
  counterStep = 1;
  counterInitialValue = 1;
  insideSalesReasonEnum = 864630002;
  defaultType = this.typesList[0];
  defaultLocation = this.locationsList[0];
  defaultColor = this.colors[this.defaultType.name][0];
  defaultStyle = this.styles[this.defaultColor.name][0];
  defaultSlidingStyle = this.slidingStyles[this.defaultColor.name][0];
  DOOR_TYPES = DOOR_TYPES;
  DOOR_STYLES = DOOR_STYLES;

  constructor(
    private fb: FormBuilder,
    private dataService: DataService,
    public nextButtonState: NextButtonStateService
  ) {}

  ngOnInit(): void {
    if (this.dataService.id) {
      const sub = this.dataService
        .getStepsData(this.dataService.id)
        .subscribe((data) => {
          this.prepopulateStepData(data);
          this.previouslyFilledData?.projectDetails
            ? this.checkFormValidity()
            : this.buildForm();
        });
      this.subscription.add(sub);
    }
  }

  switchColor(color: Option, item: FormGroup) {
    item.patchValue({
      color: color.name,
    });
  }

  switchStyle(style: Option, item: FormGroup) {
    item.patchValue({
      style: style.name,
    });
  }

  buildForm() {
    this.createNewWindowForm(this.stylesList[0]);
  }

  onCounterValueChange(
    {
      value,
      valid,
    }: {
      value: number;
      valid: boolean;
    },
    type: Item,
    item: FormGroup
  ): void {
    if (type === this.itemTypes.WINDOWS) {
      item.patchValue(
        {
          amount: value,
        },
        { emitEvent: true }
      );
    }
    this.checkFormValidity();
  }

  getOptionsObject(value: string) {
    return this.stylesList.find((item: WindowOption) => item.text === value);
  }

  createNewWindowForm(option: WindowStyles, value?: any) {
    const newGroup = this.fb.group({
      style: [option.text, [Validators.required]],
      amount: [
        1,
        [
          Validators.required,
          Validators.min(this.counterMinValue),
          Validators.max(this.counterMaxValue),
        ],
      ],
      options: this.fb.array([]),
    });
    this.windowForms.push(newGroup);
    const checkboxOptions = option?.itemsList;
    const optionsFormArray = newGroup.get('options') as FormArray;
    optionsFormArray.clear();
    checkboxOptions.forEach(() => {
      optionsFormArray.push(this.fb.control(false));
    });

    if (value?.amount) {
      newGroup.patchValue({
        amount: value.amount,
      });
    }
    if (value?.options.length) {
      let optionsToPatch = [];

      option.itemsList.forEach((opt: any) => {
        optionsToPatch.push(!!value.options.includes(opt.text));
      });
      newGroup.patchValue({
        options: optionsToPatch,
      });
    }

    this.subscription.add(
      newGroup.valueChanges.subscribe((value) => {
        this.checkFormValidity();
      })
    );
    this.checkFormValidity();
  }

  createNewDoorForm(type, location, color, style, value?: any) {
    const newGroup = this.fb.group({
      type: [type.name, [Validators.required]],
      location: [location.name, [Validators.required]],
      color: [color?.name ?? null],
      style: [style.name, [Validators.required]],
      customStyle: [
        value?.style === DOOR_STYLES.CUSTOM ? DOOR_STYLES.CUSTOM : null,
      ],
      styleComments: [
        value?.style === DOOR_STYLES.CUSTOM ? value?.styleComments || '' : '',
        [Validators.maxLength(3000)],
      ],
    });
    this.doorForms.push(newGroup);

    if (value?.type) {
      newGroup.patchValue({
        type: value.type,
      });
    }
    if (value?.location) {
      newGroup.patchValue({
        location: value.location,
      });
    }
    if (value?.color) {
      newGroup.patchValue({
        color: value.color,
      });
    }
    if (value?.style) {
      newGroup.patchValue({
        style: value.style,
      });
    }

    this.subscription.add(
      newGroup.valueChanges.subscribe((value) => {
        this.checkFormValidity();
      })
    );
    this.subscription.add(
      newGroup.controls['customStyle'].valueChanges.subscribe((value) => {
        if (value) {
          newGroup.patchValue({
            style: DOOR_STYLES.CUSTOM,
            color: null,
          });
        } else {
          if (newGroup.controls['type'].value === DOOR_TYPES.SLIDING) {
            newGroup.patchValue({
              style: this.defaultSlidingStyle.name,
              color: this.defaultColor.name,
              styleComments: '',
            });
          } else {
            newGroup.patchValue({
              style: this.defaultStyle.name,
              color: this.defaultColor.name,
              styleComments: '',
            });
          }
        }
      })
    );
    this.checkFormValidity();
  }

  onChange(e, item) {
    const optionsFormArray = item.get('options') as FormArray;
    optionsFormArray.clear();
    const obj = this.stylesList.find((item: WindowOption) => item.text === e);
    obj.itemsList.forEach(() => {
      optionsFormArray.push(this.fb.control(false));
    });
  }

  onDoorTypeChange(type: string, item: FormGroup) {
    if (
      type === DOOR_TYPES.SLIDING &&
      item.controls['color'].value === DOOR_COLORS.GREY
    ) {
      item.patchValue({
        color: this.defaultColor.name,
      });
    }

    const isPreviosStyles = this.styles[item.controls['color'].value].find(
      (style) => style.name === item.controls['style'].value
    );
    const isPreviosSlidingStyles = this.slidingStyles[
      item.controls['color'].value
    ].find((style) => style.name === item.controls['style'].value);
    if (type === DOOR_TYPES.SLIDING && !isPreviosSlidingStyles) {
      item.patchValue({
        style: this.defaultSlidingStyle.name,
      });
    }
    if (type !== DOOR_TYPES.SLIDING && !isPreviosStyles) {
      item.patchValue({
        style: this.defaultStyle.name,
      });
    }
  }

  subscribeToStyleChanges(): void {
    this.windowForms.forEach((form) => {});
  }

  addItem = (item): void => {
    if (item === this.itemTypes.WINDOWS) {
      this.createNewWindowForm(this.stylesList[0]);
    } else if (item === this.itemTypes.DOORS) {
      this.createNewDoorForm(
        this.defaultType,
        this.defaultLocation,
        this.defaultColor,
        this.defaultStyle
      );
    }
  };

  duplicate(item) {
    this.createNewDoorForm(
      this.defaultType,
      this.defaultLocation,
      this.defaultColor,
      this.defaultStyle,
      item.value
    );
  }

  removeItem(type: string, index: number): void {
    if (type === this.itemTypes.WINDOWS) {
      this.windowForms.splice(index, 1);
    } else if (type === this.itemTypes.DOORS) {
      this.doorForms.splice(index, 1);
    }

    this.checkFormValidity();
  }

  checkFormValidity() {
    setTimeout((_) => {
      this.isWindowsFormInvalid = this.windowForms.some((form) => {
        return form.invalid;
      });
      this.isDoorsFormInvalid = this.doorForms.some((form) => {
        return form.invalid;
      });
      this.nextButtonState.emitActivateNext(
        !this.isWindowsFormInvalid && !this.isDoorsFormInvalid
      );
    });
  }

  updateFormValues() {
    if (this.previouslyFilledData?.projectDetails?.windows)
      this.windowForms = [];
    if (this.previouslyFilledData?.projectDetails?.doors) this.doorForms = [];
    this.previouslyFilledData?.projectDetails?.windows?.forEach((item: any) => {
      const style = this.stylesList.find(
        (style: WindowOption) => style.text === item.style
      );
      this.createNewWindowForm(style, item);
    });
    this.previouslyFilledData?.projectDetails?.doors?.forEach((item: any) => {
      const type = this.typesList.find((type: any) => type.name === item.type);
      const location = this.locationsList.find(
        (location: any) => location.name === item.location
      );
      const color =
        item?.color === null
          ? null
          : this.colors[type.name].find(
              (color: any) => color.name === item.color
            );
      const style =
        item.style === DOOR_STYLES.CUSTOM
          ? DOOR_STYLES.CUSTOM
          : type.name === DOOR_TYPES.SLIDING
          ? this.slidingStyles[color.name].find(
              (style: any) => style.name === item.style
            )
          : this.styles[color.name].find(
              (style: any) => style.name === item.style
            );
      this.createNewDoorForm(type, location, color, style, item);
    });
  }

  prepopulateStepData(data: FormData) {
    if (data?.appointmentRequest?.projectDetails) {
      this.previouslyFilledData = {
        projectDetails: data?.appointmentRequest?.projectDetails,
      };
    }
    this.updateFormValues();
  }

  onClickNext = (): Observable<any> => {
    return this.saveData(NAVIGATION_MAP[PAGES.ADDRESS].nextButton.route, true);
  };

  onClickBack = (): Observable<any> => {
    return !this.isWindowsFormInvalid && !this.isDoorsFormInvalid
      ? this.saveData(NAVIGATION_MAP[PAGES.ADDRESS].backButton.route)
      : of(true);
  };

  saveData(nextPage: string, isNext?: boolean): Observable<any> {
    const stepsData = { ...this.dataService.stepsData };

    let windows = [];
    this.windowForms.forEach((item: any) => {
      windows.push(item.value);
    });

    let doors = [];
    this.doorForms.forEach((item: any) => {
      doors.push(item.value);
    });

    if (windows) {
      stepsData.appointmentRequest.projectDetails =
        stepsData.appointmentRequest.projectDetails || {};
      if (windows.length) {
        const transformedWindows = windows.map((window) => {
          const style = this.stylesList.find(
            (style: WindowOption) => style.text === window.style
          );
          if (!style) return { ...window, options: [] };

          const selectedOptions = style.itemsList
            .filter((_, index) => window.options[index])
            .map((option) => option.text);
          return { ...window, options: selectedOptions };
        });
        stepsData.appointmentRequest.projectDetails['windows'] =
          transformedWindows;
      } else {
        stepsData.appointmentRequest.projectDetails['windows'] = null;
      }
    }

    if (doors) {
      stepsData.appointmentRequest.projectDetails =
        stepsData.appointmentRequest.projectDetails || {};

      if (doors.length) {
        const updatedDoors = doors.map(({ customStyle, ...rest }) => rest);
        stepsData.appointmentRequest.projectDetails['doors'] = updatedDoors;
      } else {
        stepsData.appointmentRequest.projectDetails['doors'] = null;
      }
    }

    if (!stepsData?.appointmentRequest?.projectDetails)
      stepsData.appointmentRequest.projectDetails = null;

    if (isNext) {
      stepsData.appointmentRequest.insideSales = true;
      stepsData.status = 'InsideSales';
    }

    const sessionData = JSON.parse(stepsData.sessionData);
    sessionData.nextPage = nextPage;
    stepsData.sessionData = JSON.stringify(sessionData);
    return this.dataService.saveStepsData(stepsData).pipe(
      map((res) => {
        return true;
      })
    );
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
  }
}
